import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getDefaultIncubationFields } from '../../services/IncubationServices';
import { createIncubation, getIncubation, updateIncubation } from '../../controllers/incubations';
import { getIncubationUserAccess } from '../../controllers/users';
import { displayStringToKey, keyToDisplayString } from '../../utils/StringUtils';
import Card from '../../components/ui/Card/Card';
import Input from '../../components/ui/Input/Input';
import DatePicker from '../../components/ui/DatePicker/DatePicker';
import Button from '../../components/ui/Button/Button';
import Textarea from '../../components/ui/Textarea/Textarea';
import Radio from '../../components/ui/Radio/Radio';
import Select from '../../components/ui/Select/Select';
import Spinner from '../../components/ui/Spinner/Spinner';
import { useToast } from '../../contexts/ToastContext';
import { useUser } from '../../contexts/UserContext';
import './CreateIncubationScreen.css';

const CreateIncubationScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess, showWarning } = useToast();
  const { hasGlobalFunctionality, hasIncubationPermission } = useUser();
  
  // Get incubation ID and mode from location state
  const id = location.state?.incubationId;
  const mode = location.state?.mode || 'create';
  const finalMode = mode;
  
  const [formFields, setFormFields] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // In edit mode, start as loading so the dirty-snapshot effect waits until the
  // server data is populated (otherwise it would snapshot empty defaults and
  // every loaded value would then read as "unsaved").
  const [loading, setLoading] = useState(mode === 'edit');

  const [incubationName, setIncubationName]           = useState('');
  const [incubationStatus, setIncubationStatus]       = useState('pre_launch');
  const [incubationStartDate, setIncubationStartDate] = useState(new Date());
  const [incubationEndDate, setIncubationEndDate]     = useState(new Date());

  const [incubationOverview, setIncubationOverview]   = useState('');
  const [incubationPurpose, setIncubationPurpose]     = useState('');
  const [targetCompletes, setTargetCompletes]         = useState('');

  const [incubationInclusionCriteria, setIncubationInclusionCriteria] = useState([]);
  const [newInclusionCriteria, setNewInclusionCriteria] = useState('');
  const [editingInclusionCriteria, setEditingInclusionCriteria] = useState([]);

  const [incubationExclusionCriteria, setIncubationExclusionCriteria] = useState([]);
  const [newExclusionCriteria, setNewExclusionCriteria] = useState('');
  const [editingExclusionCriteria, setEditingExclusionCriteria] = useState([]);

  const [selectedDataFields, setSelectedDataFields] = useState([]);
  const [newDataField, setNewDataField]             = useState('');
  const [dataFieldType, setDataFieldType]           = useState('text');
  const [editingDataFields, setEditingDataFields]   = useState([]);
  const [dataFieldConfirmRemove, setDataFieldConfirmRemove] = useState({});
  const [inclusionCriteriaConfirmRemove, setInclusionCriteriaConfirmRemove] = useState({});
  const [exclusionCriteriaConfirmRemove, setExclusionCriteriaConfirmRemove] = useState({});
  const [newFieldChoices, setNewFieldChoices]       = useState(['', '']);
  const [editingFieldChoices, setEditingFieldChoices] = useState({});
  const [choiceValidationErrors, setChoiceValidationErrors] = useState({});

  // Calculated fields state
  const [calculatedFields, setCalculatedFields] = useState([]);
  const [showAgeField, setShowAgeField] = useState(false);
  const [ageFieldName, setAgeFieldName] = useState('Pet Age');
  const [ageFieldDateSource, setAgeFieldDateSource] = useState('');
  const [showWeightField, setShowWeightField] = useState(false);
  const [weightFieldName, setWeightFieldName] = useState('Pet Weight (kg)');
  const [weightFieldSource, setWeightFieldSource] = useState('');

  // User access (read-only) — populated in edit mode only
  const [incubationUsers, setIncubationUsers] = useState([]);
  const [incubationUsersLoading, setIncubationUsersLoading] = useState(false);

  // UI state for two-column layout
  const [criteriaTab, setCriteriaTab] = useState('inclusion');

  // Field edit modal state — stores index into editingDataFields
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const closeFieldModal = () => setEditingFieldIndex(null);
  const openFieldModal = (idx) => setEditingFieldIndex(idx);

  // Dirty-state tracking — snapshot of initial/saved form state
  const [initialSnapshot, setInitialSnapshot] = useState(null);

  // New-field modal state
  const [creatingField, setCreatingField] = useState(false);
  const openCreateFieldModal = () => {
    setNewDataField('');
    setDataFieldType('text');
    setNewFieldChoices(['', '']);
    setChoiceValidationErrors({});
    setCreatingField(true);
  };
  const closeCreateFieldModal = () => setCreatingField(false);
  const handleCreateFieldSubmit = () => {
    if (!newDataField.trim()) return;
    if (dataFieldType === 'choices') {
      const errs = {};
      let hasErr = false;
      if (!newFieldChoices[0]?.trim()) { errs[0] = true; hasErr = true; }
      if (!newFieldChoices[1]?.trim()) { errs[1] = true; hasErr = true; }
      if (hasErr) {
        setChoiceValidationErrors(errs);
        return;
      }
    }
    handleAddDataField();
    setCreatingField(false);
  };

  // Data field type options for dropdown
  const dataFieldTypeOptions = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'yes_no', label: 'Yes/No' },
    { value: 'choices', label: 'Choices' }
  ];

  useEffect(() => {
    const defaultFields = getDefaultIncubationFields();
    setFormFields(defaultFields);
  }, []);

  // Initialize default data fields when in create mode
  useEffect(() => {
    if (finalMode === 'create' && selectedDataFields.length === 0) {
      const defaultDataFields = [
        { name: displayStringToKey('Source'), displayName: 'Source', type: 'text', display_position: 1, originalKey: displayStringToKey('Source') },
        { name: displayStringToKey('Source Contact'), displayName: 'Source Contact', type: 'text', display_position: 2, originalKey: displayStringToKey('Source Contact') },
        { name: displayStringToKey('Owner First Name'), displayName: 'Owner First Name', type: 'text', display_position: 3, originalKey: displayStringToKey('Owner First Name') },
        { name: displayStringToKey('Owner Last Name'), displayName: 'Owner Last Name', type: 'text', display_position: 4, originalKey: displayStringToKey('Owner Last Name') },
        { name: displayStringToKey('Owner First Name - Alternate'), displayName: 'Owner First Name - Alternate', type: 'text', display_position: 5, originalKey: displayStringToKey('Owner First Name - Alternate') },
        { name: displayStringToKey('Owner Last Name - Alternate'), displayName: 'Owner Last Name - Alternate', type: 'text', display_position: 6, originalKey: displayStringToKey('Owner Last Name - Alternate') },
        { name: displayStringToKey('Owner Street Address'), displayName: 'Owner Street Address', type: 'text', display_position: 7, originalKey: displayStringToKey('Owner Street Address') },
        { name: displayStringToKey('Owner City'), displayName: 'Owner City', type: 'text', display_position: 8, originalKey: displayStringToKey('Owner City') },
        { name: displayStringToKey('Owner State'), displayName: 'Owner State', type: 'text', display_position: 9, originalKey: displayStringToKey('Owner State') },
        { name: displayStringToKey('Owner Zip Code'), displayName: 'Owner Zip Code', type: 'text', display_position: 10, originalKey: displayStringToKey('Owner Zip Code') },
        { name: displayStringToKey('Owner Timezone'), displayName: 'Owner Timezone', type: 'text', display_position: 11, originalKey: displayStringToKey('Owner Timezone') },
        { name: displayStringToKey('Owner Email Address'), displayName: 'Owner Email Address', type: 'text', display_position: 12, originalKey: displayStringToKey('Owner Email Address') },
        { name: displayStringToKey('Owner Email Address - Alternate'), displayName: 'Owner Email Address - Alternate', type: 'text', display_position: 13, originalKey: displayStringToKey('Owner Email Address - Alternate') },
        { name: displayStringToKey('Owner Phone Number'), displayName: 'Owner Phone Number', type: 'text', display_position: 14, originalKey: displayStringToKey('Owner Phone Number') },
        { name: displayStringToKey('Owner Phone Number - Alternate'), displayName: 'Owner Phone Number - Alternate', type: 'text', display_position: 15, originalKey: displayStringToKey('Owner Phone Number - Alternate') },
        { name: displayStringToKey('Owner consent form - signed?'), displayName: 'Owner consent form - signed?', type: 'yes_no', display_position: 16, originalKey: displayStringToKey('Owner consent form - signed?') },
        { name: displayStringToKey('Owner consent form - date fully signed'), displayName: 'Owner consent form - date fully signed', type: 'date', display_position: 17, originalKey: displayStringToKey('Owner consent form - date fully signed') },
        { name: displayStringToKey('Pet Type'), displayName: 'Pet Type', type: 'choices', choices: ['Dog', 'Cat', 'Other'], display_position: 18, originalKey: displayStringToKey('Pet Type') },
        { name: displayStringToKey('Pet First Name'), displayName: 'Pet First Name', type: 'text', display_position: 19, originalKey: displayStringToKey('Pet First Name') },
        { name: displayStringToKey('Pet First Name - FKA'), displayName: 'Pet First Name - FKA', type: 'text', display_position: 20, originalKey: displayStringToKey('Pet First Name - FKA') },
        { name: displayStringToKey('Pet Birthdate - Reported'), displayName: 'Pet Birthdate - Reported', type: 'date', display_position: 21, originalKey: displayStringToKey('Pet Birthdate - Reported') },
        { name: displayStringToKey('Pet Sex'), displayName: 'Pet Sex', type: 'choices', choices: ['Male', 'Female'], display_position: 23, originalKey: displayStringToKey('Pet Sex') },
        { name: displayStringToKey('Pet Spayed / Neutered / Intact'), displayName: 'Pet Spayed / Neutered / Intact', type: 'choices', choices: ['Spayed', 'Neutered', 'Intact'], display_position: 24, originalKey: displayStringToKey('Pet Spayed / Neutered / Intact') },
        { name: displayStringToKey('Pet Breed Reported - Primary'), displayName: 'Pet Breed Reported - Primary', type: 'text', display_position: 25, originalKey: displayStringToKey('Pet Breed Reported - Primary') },
        { name: displayStringToKey('Pet Breed Reported - Secondary'), displayName: 'Pet Breed Reported - Secondary', type: 'text', display_position: 26, originalKey: displayStringToKey('Pet Breed Reported - Secondary') },
        { name: displayStringToKey('Pet Weight (lbs)'), displayName: 'Pet Weight (lbs)', type: 'number', display_position: 27, originalKey: displayStringToKey('Pet Weight (lbs)') },
        { name: displayStringToKey('Body Condition Score'), displayName: 'Body Condition Score', type: 'choices', choices: ['1', '2', '3', '4', '5', '6', '7', '8', '9'], display_position: 29, originalKey: displayStringToKey('Body Condition Score') },
        { name: displayStringToKey('Owner Address - Alternative'), displayName: 'Owner Address - Alternative', type: 'text', display_position: 30, originalKey: displayStringToKey('Owner Address - Alternative') },
        { name: displayStringToKey('Date Of Completion'), displayName: 'Date Of Completion', type: 'date', display_position: 31, originalKey: displayStringToKey('Date Of Completion') },
        { name: displayStringToKey('Completed'), displayName: 'Completed', type: 'yes_no', display_position: 32, originalKey: displayStringToKey('Completed') },
        { name: displayStringToKey('VIP'), displayName: 'VIP', type: 'yes_no', display_position: 33, originalKey: displayStringToKey('VIP') },
      ];
      setSelectedDataFields(defaultDataFields);
      setEditingDataFields(defaultDataFields.map(f => ({ ...f, isEditing: false })));

      // Initialize default calculated fields
      if (calculatedFields.length === 0) {
        const defaultCalculatedFields = [
          {
            name: 'Pet Age',
            type: 'calculated',
            calculation: 'pet_age',
            dateSource: displayStringToKey('Pet Birthdate - Reported'),
            display_position: 22
          },
          {
            name: 'Pet Weight (kg)',
            type: 'calculated',
            calculation: 'lbs_to_kg',
            sourceField: displayStringToKey('Pet Weight (lbs)'),
            display_position: 28
          }
        ];
        setCalculatedFields(defaultCalculatedFields);
      }
    }
  }, [finalMode]);

  // Load existing incubation data for edit mode
  useEffect(() => {
    const loadIncubationData = async () => {
      if (finalMode === 'edit') {
        if (!id) {
          console.error('No incubation ID provided for edit mode');
          return;
        }

        try {
          setLoading(true);
          const response = await getIncubation({ incubation_id: id });

          if (response.data && response.data.success && response.data.incubation) {
            const incubation = response.data.incubation;
            
            // Populate form fields with existing data
            setIncubationName(incubation.name || '');
            setIncubationStatus(incubation.status || 'pre_launch');
            setIncubationOverview(incubation.overview || '');
            setIncubationPurpose(incubation.purpose || '');
            setTargetCompletes(incubation.target_completes?.toString() || '');

            // Handle dates
            if (incubation.start_date) {
              setIncubationStartDate(new Date(incubation.start_date));
            }
            if (incubation.end_date) {
              setIncubationEndDate(new Date(incubation.end_date));
            }
            
            // Handle inclusion criteria
            if (incubation.inclusion_criteria && Array.isArray(incubation.inclusion_criteria)) {
              setIncubationInclusionCriteria(incubation.inclusion_criteria);
              setEditingInclusionCriteria(incubation.inclusion_criteria.map(criteria => ({
                ...criteria,
                isEditing: false
              })));
            }
            
            // Handle exclusion criteria
            if (incubation.exclusion_criteria && Array.isArray(incubation.exclusion_criteria)) {
              setIncubationExclusionCriteria(incubation.exclusion_criteria);
              setEditingExclusionCriteria(incubation.exclusion_criteria.map(criteria => ({
                ...criteria,
                isEditing: false
              })));
            }
            
            // Handle data fields - keep original key in name, store display name separately
            if (incubation.data_fields && Array.isArray(incubation.data_fields)) {
              const fieldsWithDisplayNames = incubation.data_fields.map(field => ({
                ...field,
                name: field.name, // Keep original field key (e.g., "pet_age")
                displayName: field.display_name || keyToDisplayString(field.name), // Store display name separately
                originalKey: field.name // Keep original key for backend
              }));
              setSelectedDataFields(fieldsWithDisplayNames);
              setEditingDataFields(fieldsWithDisplayNames.map(field => ({
                ...field,
                isEditing: false
              })));
            }
            
            // Handle calculated fields - convert backend format to UI format
            if (incubation.calculated_fields && Array.isArray(incubation.calculated_fields)) {
              const calculatedFieldsForUI = incubation.calculated_fields.map(field => {
                const calculationType = field.calculation_type || 'pet_age'; // Default to pet_age for backward compatibility
                const uiField = {
                  name: field.display_name || keyToDisplayString(field.name),
                  type: 'calculated',
                  calculation: calculationType,
                  ...(field.display_position && { display_position: field.display_position }) // Preserve display_position if it exists
                };
                
                // Set the appropriate source field property based on calculation type
                if (calculationType === 'lbs_to_kg') {
                  uiField.sourceField = field.source_field_name;
                } else {
                  uiField.dateSource = field.source_field_name;
                }
                
                return uiField;
              });
              setCalculatedFields(calculatedFieldsForUI);
            }
            
          } else {
            console.error('Failed to load incubation data:', response.data?.error || 'Unknown error');
            showError('Failed to load incubation data. Please try again.');
            navigate('/home', { state: { activeSection: 'Incubations' } });
          }
        } catch (error) {
          console.error('Error loading incubation data:', error);
          showError('Failed to load incubation data. Please try again.');
          navigate('/home', { state: { activeSection: 'Incubations' } });
        } finally {
          setLoading(false);
        }
      }
    };

    loadIncubationData();
  }, [id, finalMode, navigate]);

  useEffect(() => {
    if (finalMode !== 'edit' || !id) return;
    let cancelled = false;
    const loadUsers = async () => {
      setIncubationUsersLoading(true);
      try {
        const response = await getIncubationUserAccess({ incubation_id: id });
        if (cancelled) return;
        if (response?.data?.success) {
          setIncubationUsers(response.data.users || []);
        } else {
          console.error('Failed to load incubation user access:', response?.data?.error);
          setIncubationUsers([]);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading incubation user access:', error);
        setIncubationUsers([]);
      } finally {
        if (!cancelled) setIncubationUsersLoading(false);
      }
    };
    loadUsers();
    return () => { cancelled = true; };
  }, [id, finalMode]);

  const handleAddInclusionCriteria = () => {
    if (newInclusionCriteria.trim()) {
      const newCriteria = { name: newInclusionCriteria.trim() };
      setIncubationInclusionCriteria([...incubationInclusionCriteria, newCriteria]);
      setEditingInclusionCriteria([...editingInclusionCriteria, {
        ...newCriteria,
        isEditing: false
      }]);
      setNewInclusionCriteria('');
    }
  };

  const handleRemoveInclusionCriteria = (indexToRemove) => {
    setIncubationInclusionCriteria(incubationInclusionCriteria.filter((_, index) => index !== indexToRemove));
    setEditingInclusionCriteria(editingInclusionCriteria.filter((_, index) => index !== indexToRemove));

    // Clear confirmation state
    const newConfirmState = { ...inclusionCriteriaConfirmRemove };
    delete newConfirmState[indexToRemove];
    setInclusionCriteriaConfirmRemove(newConfirmState);
  };

  const handleInclusionCriteriaRemoveClick = (index) => {
    if (inclusionCriteriaConfirmRemove[index]) {
      // Second click - actually remove
      handleRemoveInclusionCriteria(index);
    } else {
      // First click - show confirmation
      setInclusionCriteriaConfirmRemove({
        ...inclusionCriteriaConfirmRemove,
        [index]: true
      });
    }
  };

  const handleInclusionCriteriaKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddInclusionCriteria();
    }
  };

  const handleAddExclusionCriteria = () => {
    if (newExclusionCriteria.trim()) {
      const newCriteria = { name: newExclusionCriteria.trim() };
      setIncubationExclusionCriteria([...incubationExclusionCriteria, newCriteria]);
      setEditingExclusionCriteria([...editingExclusionCriteria, {
        ...newCriteria,
        isEditing: false
      }]);
      setNewExclusionCriteria('');
    }
  };

  const handleRemoveExclusionCriteria = (indexToRemove) => {
    setIncubationExclusionCriteria(incubationExclusionCriteria.filter((_, index) => index !== indexToRemove));
    setEditingExclusionCriteria(editingExclusionCriteria.filter((_, index) => index !== indexToRemove));

    // Clear confirmation state
    const newConfirmState = { ...exclusionCriteriaConfirmRemove };
    delete newConfirmState[indexToRemove];
    setExclusionCriteriaConfirmRemove(newConfirmState);
  };

  const handleExclusionCriteriaRemoveClick = (index) => {
    if (exclusionCriteriaConfirmRemove[index]) {
      // Second click - actually remove
      handleRemoveExclusionCriteria(index);
    } else {
      // First click - show confirmation
      setExclusionCriteriaConfirmRemove({
        ...exclusionCriteriaConfirmRemove,
        [index]: true
      });
    }
  };

  const handleExclusionCriteriaKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddExclusionCriteria();
    }
  };

  const handleExclusionCriteriaNameChange = (index, name) => {
    const updatedCriteria = [...editingExclusionCriteria];
    updatedCriteria[index].name = name;
    setEditingExclusionCriteria(updatedCriteria);
    
    // Also update exclusionCriteria directly
    const updatedSelectedCriteria = [...incubationExclusionCriteria];
    updatedSelectedCriteria[index] = {
      ...updatedSelectedCriteria[index],
      name: name
    };
    setIncubationExclusionCriteria(updatedSelectedCriteria);
  };

  const handleInclusionCriteriaNameChange = (index, name) => {
    const updatedCriteria = [...editingInclusionCriteria];
    updatedCriteria[index].name = name;
    setEditingInclusionCriteria(updatedCriteria);
    
    // Also update inclusionCriteria directly
    const updatedSelectedCriteria = [...incubationInclusionCriteria];
    updatedSelectedCriteria[index] = {
      ...updatedSelectedCriteria[index],
      name: name
    };
    setIncubationInclusionCriteria(updatedSelectedCriteria);
  };

  const validateChoices = () => {
    if (dataFieldType === 'choices') {
      const errors = {};
      let hasErrors = false;
      
      // Check if first two choices are filled
      if (!newFieldChoices[0]?.trim()) {
        errors[0] = true;
        hasErrors = true;
      }
      if (!newFieldChoices[1]?.trim()) {
        errors[1] = true;
        hasErrors = true;
      }
      
      setChoiceValidationErrors(errors);
      return !hasErrors;
    }
    return true;
  };

  const handleAddDataField = () => {
    if (newDataField.trim()) {
      // Validate choices if type is choices
      if (!validateChoices()) {
        return; // Don't add the field if validation fails
      }
      
      const fieldKey = displayStringToKey(newDataField.trim());
      const newField = {
        name: fieldKey,                                // Convert to key format
        displayName: newDataField.trim(),              // Store display name
        type: dataFieldType,
        originalKey: fieldKey,                         // Keep original key for backend
        ...(dataFieldType === 'choices' && { choices: newFieldChoices.filter(Boolean) })
      };
      setSelectedDataFields([...selectedDataFields, newField]);
      setEditingDataFields([...editingDataFields, {
        ...newField,
        isEditing: false
      }]);

      setNewDataField('');
      setDataFieldType('text');
      setNewFieldChoices(['', '']);
      setChoiceValidationErrors({});
    }
  };

  const handleRemoveDataField = (indexToRemove) => {
    setSelectedDataFields(selectedDataFields.filter((_, index) => index !== indexToRemove));
    setEditingDataFields(editingDataFields.filter((_, index) => index !== indexToRemove));

    // Clear confirmation state
    const newConfirmState = { ...dataFieldConfirmRemove };
    delete newConfirmState[indexToRemove];
    setDataFieldConfirmRemove(newConfirmState);
  };

  const handleDataFieldRemoveClick = (index) => {
    if (dataFieldConfirmRemove[index]) {
      // Second click - actually remove
      handleRemoveDataField(index);
    } else {
      // First click - show confirmation
      setDataFieldConfirmRemove({
        ...dataFieldConfirmRemove,
        [index]: true
      });
    }
  };

  const handleDataFieldKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddDataField();
    }
  };


  const handleDataFieldNameChange = (index, name) => {
    const updatedFields = [...editingDataFields];
    updatedFields[index].displayName = name; // This is the display name for UI
    updatedFields[index].name = displayStringToKey(name); // Update the field key as well
    setEditingDataFields(updatedFields);
    
    // Also update selectedDataFields directly
    const updatedSelectedFields = [...selectedDataFields];
    updatedSelectedFields[index] = {
      ...updatedSelectedFields[index],
      displayName: name, // This is the display name for UI
      name: displayStringToKey(name) // Update the field key as well
    };
    setSelectedDataFields(updatedSelectedFields);
  };

  const handleDataFieldTypeChange = (index, type) => {
    const updatedFields = [...editingDataFields];
    updatedFields[index].type = type;
    setEditingDataFields(updatedFields);
    
    // Also update selectedDataFields directly
    const updatedSelectedFields = [...selectedDataFields];
    updatedSelectedFields[index] = {
      ...updatedSelectedFields[index],
      type: type
    };
    setSelectedDataFields(updatedSelectedFields);
  };

  const handleDataFieldTypeChangeForNew = (type) => {
    setDataFieldType(type);
    // Reset choice inputs when switching away from choices
    if (type !== 'choices') {
      setNewFieldChoices(['', '']);
    }
  };

  const handleNewFieldChoiceChange = (choiceIndex, value) => {
    const updatedChoices = [...newFieldChoices];
    updatedChoices[choiceIndex] = value;
    setNewFieldChoices(updatedChoices);
    
    // Clear validation error for this choice when user starts typing
    if (choiceIndex < 2 && value.trim()) {
      setChoiceValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[choiceIndex];
        return newErrors;
      });
    }
  };

  const handleDeleteNewChoice = (choiceIndex) => {
    const updatedChoices = [...newFieldChoices];
    updatedChoices.splice(choiceIndex, 1);
    // Ensure we always have at least 2 empty slots
    while (updatedChoices.length < 2) {
      updatedChoices.push('');
    }
    setNewFieldChoices(updatedChoices);
  };

  const handleAddNewChoice = () => {
    setNewFieldChoices([...newFieldChoices, '']);
  };

  const handleFieldChoiceChange = (fieldIndex, choiceIndex, value) => {
    const updatedChoices = { ...editingFieldChoices };
    if (!updatedChoices[fieldIndex]) {
      updatedChoices[fieldIndex] = ['', ''];
    }
    updatedChoices[fieldIndex][choiceIndex] = value;
    setEditingFieldChoices(updatedChoices);
    
    // Also update the field in editingDataFields
    const updatedFields = [...editingDataFields];
    updatedFields[fieldIndex].choices = updatedChoices[fieldIndex].filter(Boolean);
    setEditingDataFields(updatedFields);
  };

  const handleDeleteFieldChoice = (fieldIndex, choiceIndex) => {
    const updatedChoices = { ...editingFieldChoices };
    if (!updatedChoices[fieldIndex]) {
      updatedChoices[fieldIndex] = ['', ''];
    }
    // Remove the choice at the specified index
    updatedChoices[fieldIndex].splice(choiceIndex, 1);
    // Ensure we always have at least 2 empty slots
    while (updatedChoices[fieldIndex].length < 2) {
      updatedChoices[fieldIndex].push('');
    }
    setEditingFieldChoices(updatedChoices);
    
    // Also update the field in editingDataFields
    const updatedFields = [...editingDataFields];
    updatedFields[fieldIndex].choices = updatedChoices[fieldIndex].filter(Boolean);
    setEditingDataFields(updatedFields);
  };

  const handleAddFieldChoice = (fieldIndex) => {
    const updatedChoices = { ...editingFieldChoices };
    if (!updatedChoices[fieldIndex]) {
      updatedChoices[fieldIndex] = ['', ''];
    }
    updatedChoices[fieldIndex].push('');
    setEditingFieldChoices(updatedChoices);
  };

  // Calculated fields handlers
  const handleAddAgeField = () => {
    setAgeFieldName('Pet Age');
    setAgeFieldDateSource('');
    setShowAgeField(true);
  };

  const handleAgeFieldNameChange = (name) => {
    setAgeFieldName(name);
  };

  const handleAgeFieldDateSourceChange = (dateField) => {
    setAgeFieldDateSource(dateField);
    // Automatically add the calculated field when a date source is selected
    if (dateField && ageFieldName.trim()) {
      const newCalculatedField = {
        name: ageFieldName.trim(),
        type: 'calculated',
        calculation: 'pet_age',
        dateSource: dateField
      };
      setCalculatedFields([...calculatedFields, newCalculatedField]);
      setShowAgeField(false);
      setAgeFieldName('Pet Age');
      setAgeFieldDateSource('');
    }
  };

  const handleRemoveCalculatedField = (index) => {
    setCalculatedFields(calculatedFields.filter((_, i) => i !== index));
  };

  // Weight conversion field handlers
  const handleAddWeightField = () => {
    setWeightFieldName('Pet Weight (kg)');
    setWeightFieldSource('');
    setShowWeightField(true);
  };

  const handleWeightFieldNameChange = (name) => {
    setWeightFieldName(name);
  };

  const handleWeightFieldSourceChange = (sourceField) => {
    setWeightFieldSource(sourceField);
    // Automatically add the calculated field when a source is selected
    if (sourceField && weightFieldName.trim()) {
      const newCalculatedField = {
        name: weightFieldName.trim(),
        type: 'calculated',
        calculation: 'lbs_to_kg',
        sourceField: sourceField
      };
      setCalculatedFields([...calculatedFields, newCalculatedField]);
      setShowWeightField(false);
      setWeightFieldName('Pet Weight (kg)');
      setWeightFieldSource('');
    }
  };

  // Helper function to get date fields for dropdown
  const getDateFields = () => {
    return selectedDataFields
      .filter(field => field.type === 'date')
      .map(field => ({
        value: field.name, // field.name is now the field key (e.g., "pet_age")
        label: field.displayName // field.displayName is the display name (e.g., "Pet Birthday")
      }));
  };

  // Helper function to get number fields for dropdown
  const getNumberFields = () => {
    return selectedDataFields
      .filter(field => field.type === 'number')
      .map(field => ({
        value: field.name,
        label: field.displayName
      }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!incubationName.trim()) {
      newErrors.incubationName = 'Incubation name is required';
    }
    
    if (!incubationStatus) {
      newErrors.incubationStatus = 'Incubation status is required';
    }
    
    if (!incubationStartDate) {
      newErrors.incubationStartDate = 'Start date is required';
    }
    
    if (!incubationEndDate) {
      newErrors.incubationEndDate = 'End date is required';
    }
    
    if (incubationStartDate && incubationEndDate && incubationStartDate >= incubationEndDate) {
      newErrors.incubationEndDate = 'End date must be after start date';
    }
    
    // Overview and purpose are optional - no validation needed
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    console.log('Form submission started for mode:', finalMode);
    console.log('Form data:', {
      incubationName,
      incubationStatus,
      incubationStartDate,
      incubationEndDate,
      incubationOverview,
      incubationPurpose
    });
    
    const isValid = validateForm();
    console.log('Form validation result:', isValid);
    console.log('Validation errors:', errors);
    
    if (isValid) {
      try {
        setSubmitting(true);

        // Convert data field display names to keys for backend
        const dataFieldsWithKeys = selectedDataFields.map(field => ({
          ...field,
          name: field.name, // field.name is already the field key
          display_name: field.displayName // Use the display name from UI
        }));

        // Process calculated fields for backend
        const calculatedFieldsForBackend = calculatedFields.map(field => ({
          name: displayStringToKey(field.name), // Convert display name to key format
          display_name: field.name,
          type: "text", // Calculated fields are displayed as text
          source_field_name: field.dateSource || field.sourceField, // Support both date and number sources
          calculation_type: field.calculation, // Store the calculation type
          ...(field.display_position && { display_position: field.display_position }) // Include display_position if it exists
        }));

        const incubationData = {
          name: incubationName.trim(),
          status: incubationStatus,
          start_date: incubationStartDate.toISOString(),
          end_date: incubationEndDate.toISOString(),
          overview: incubationOverview.trim(),
          purpose: incubationPurpose.trim(),
          target_completes: targetCompletes ? parseInt(targetCompletes, 10) : null,
          inclusion_criteria: incubationInclusionCriteria,
          exclusion_criteria: incubationExclusionCriteria,
          data_fields: dataFieldsWithKeys,
          calculated_fields: calculatedFieldsForBackend
        };

        console.log('Sending incubation data:', incubationData);
        
        let response;
        if (finalMode === 'edit') {
          // Use updateIncubation for edit mode
          const updateData = {
            incubation_id: id,
            ...incubationData
          };
          response = await updateIncubation(updateData);
        } else {
          response = await createIncubation(incubationData);
        }
        
        console.log('API response:', response);
        
        if (response.data && response.data.success) {
          const action = finalMode === 'edit' ? 'updated' : 'created';
          console.log(`Incubation ${action} successfully:`, response.data);
          showSuccess(`Incubation ${action} successfully!`);
          // Refresh the baseline snapshot so the form reads as clean afterwards
          setInitialSnapshot({
            name: incubationName.trim(),
            status: incubationStatus,
            startDate: incubationStartDate ? incubationStartDate.toISOString() : null,
            endDate: incubationEndDate ? incubationEndDate.toISOString() : null,
            overview: incubationOverview.trim(),
            purpose: incubationPurpose.trim(),
            targetCompletes,
            inclusion: JSON.stringify(incubationInclusionCriteria),
            exclusion: JSON.stringify(incubationExclusionCriteria),
            dataFields: JSON.stringify(selectedDataFields),
            calculatedFields: JSON.stringify(calculatedFields),
          });
          // Stay on the edit screen after a successful update.
          // For create mode, the incubation now has an ID — go back to the list.
          if (finalMode !== 'edit') {
            navigate('/home', { state: { activeSection: 'Incubations' } });
          }
        } else if (response.data && response.data.error) {
          const action = finalMode === 'edit' ? 'update' : 'create';
          showError(`Failed to ${action} incubation: ${response.data.error}`);
        } else {
          const action = finalMode === 'edit' ? 'update' : 'create';
          showError(`Failed to ${action} incubation. Unexpected response from server.`);
        }
      } catch (error) {
        console.error(`Error ${finalMode === 'edit' ? 'updating' : 'creating'} incubation:`, error);
        const action = finalMode === 'edit' ? 'update' : 'create';
        showError(`Failed to ${action} incubation. Please try again.`);
      } finally {
        setSubmitting(false);
      }
    } else {
      // Show validation errors to user
      const errorMessages = Object.values(errors);
      if (errorMessages.length > 0) {
        showWarning(`Please fix the following errors:\n${errorMessages.join('\n')}`);
      }
    }
  };

  const handleBackClick = () => {
    // Block navigation when there are unsaved changes
    if (anyDirty) {
      const ok = window.confirm('You have unsaved changes. Leave without saving?');
      if (!ok) return;
    }
    // If editing, return to incubation detail; otherwise go back
    if (finalMode === 'edit' && id) {
      navigate('/incubation', { state: { incubationId: id } });
    } else {
      navigate(-1);
    }
  };

  // Status metadata for the new sticky header chip
  const statusMeta = {
    pre_launch: { label: 'Pre Launch', className: 'status-chip-prelaunch' },
    active:     { label: 'Active',     className: 'status-chip-active' },
    completed:  { label: 'Completed',  className: 'status-chip-completed' },
  };

  // Capture initial snapshot once the form is ready (edit: after data loads; create: after defaults).
  useEffect(() => {
    if (loading) return;
    if (initialSnapshot) return;
    if (finalMode === 'create' && selectedDataFields.length === 0 && calculatedFields.length === 0) return;

    setInitialSnapshot({
      name: incubationName,
      status: incubationStatus,
      startDate: incubationStartDate ? incubationStartDate.toISOString() : null,
      endDate: incubationEndDate ? incubationEndDate.toISOString() : null,
      overview: incubationOverview,
      purpose: incubationPurpose,
      targetCompletes,
      inclusion: JSON.stringify(incubationInclusionCriteria),
      exclusion: JSON.stringify(incubationExclusionCriteria),
      dataFields: JSON.stringify(selectedDataFields),
      calculatedFields: JSON.stringify(calculatedFields),
    });
  }, [loading, initialSnapshot, finalMode,
      incubationName, incubationStatus, incubationStartDate, incubationEndDate,
      incubationOverview, incubationPurpose, targetCompletes,
      incubationInclusionCriteria, incubationExclusionCriteria,
      selectedDataFields, calculatedFields]);

  // Per-section dirty flags computed against the snapshot.
  const dirty = React.useMemo(() => {
    const clean = {
      basics: false, description: false, eligibility: false,
      fields: false, calculated: false,
    };
    if (!initialSnapshot) return clean;
    const s = initialSnapshot;
    return {
      basics:
        incubationName !== s.name ||
        incubationStatus !== s.status ||
        (incubationStartDate ? incubationStartDate.toISOString() : null) !== s.startDate ||
        (incubationEndDate ? incubationEndDate.toISOString() : null) !== s.endDate ||
        targetCompletes !== s.targetCompletes,
      description:
        incubationOverview !== s.overview ||
        incubationPurpose !== s.purpose,
      eligibility:
        JSON.stringify(incubationInclusionCriteria) !== s.inclusion ||
        JSON.stringify(incubationExclusionCriteria) !== s.exclusion,
      fields:
        JSON.stringify(selectedDataFields) !== s.dataFields,
      calculated:
        JSON.stringify(calculatedFields) !== s.calculatedFields,
    };
  }, [initialSnapshot,
      incubationName, incubationStatus, incubationStartDate, incubationEndDate,
      incubationOverview, incubationPurpose, targetCompletes,
      incubationInclusionCriteria, incubationExclusionCriteria,
      selectedDataFields, calculatedFields]);

  const dirtyCount = Object.values(dirty).filter(Boolean).length;
  const anyDirty = dirtyCount > 0;

  // Warn on browser navigation / tab close when there are unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (anyDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [anyDirty]);

  // Keyboard shortcut: Cmd/Ctrl + S to save (skipped when not dirty in edit mode)
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (submitting) return;
        if (finalMode === 'edit' && !anyDirty) return;
        handleSubmit();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitting, anyDirty, finalMode, incubationName, incubationStatus, incubationStartDate, incubationEndDate,
      incubationOverview, incubationPurpose, targetCompletes,
      incubationInclusionCriteria, incubationExclusionCriteria, selectedDataFields, calculatedFields]);

  // Combine regular data fields and calculated fields, sorted alphabetically by display name
  const allDataFields = React.useMemo(() => {
    const regularFields = selectedDataFields.map(field => ({
      ...field,
      isCalculated: false
    }));

    const calcFields = calculatedFields.map(field => ({
      ...field,
      displayName: field.name,
      isCalculated: true
    }));

    const combined = [...regularFields, ...calcFields];

    return combined.sort((a, b) =>
      (a.displayName || '').localeCompare(b.displayName || '', undefined, { sensitivity: 'base' })
    );
  }, [selectedDataFields, calculatedFields]);

  // Has calculated fields already?
  const hasPetAgeCalc = calculatedFields.some(f => f.calculation === 'pet_age');
  const hasWeightKgCalc = calculatedFields.some(f => f.calculation === 'lbs_to_kg');

  // Format the type key into a display label
  const formatTypeLabel = (type) => {
    switch (type) {
      case 'text':    return 'Text';
      case 'number':  return 'Number';
      case 'date':    return 'Date';
      case 'yes_no':  return 'Yes / No';
      case 'choices': return 'Choices';
      default:        return type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="create-incubation-screen">
        <button className="back-button-screen" onClick={handleBackClick}>
          <i className="fas fa-arrow-left"></i>
          <span>Back</span>
        </button>
        <div className="create-incubation-container">
          <Card style={{ width: '700px', margin: '0 auto', padding: '20px', overflow: 'visible' }}>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spinner full size="lg" label="Loading incubation data..." />
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const hasRequiredPermission = () => {
    if (finalMode === 'edit') {
      return hasIncubationPermission(id, 'edit_incubations');
    }
    return hasGlobalFunctionality('create_incubations');
  };

  if (!hasRequiredPermission()) {
    const featureName = finalMode === 'edit' ? 'edit incubations' : 'create incubations';
    return (
      <div className="create-incubation-screen">
        <button className="back-button-screen" onClick={handleBackClick}>
          <i className="fas fa-arrow-left"></i>
          <span>Back</span>
        </button>
        <div className="create-incubation-container">
          <Card style={{ width: '700px', margin: '0 auto', padding: '20px', overflow: 'visible' }}>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <i className="fas fa-lock" style={{ fontSize: '48px', marginBottom: '20px', color: '#6b7280' }}></i>
              <h2 style={{ marginBottom: '16px', color: '#374151' }}>Access Denied</h2>
              <p style={{ color: '#6b7280', fontSize: '16px', lineHeight: '1.5' }}>
                You do not have access to this feature.
              </p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-incubation-screen">
      {/* Sticky Top Bar */}
      <header className={`ei-topbar ${anyDirty ? 'ei-topbar-dirty' : ''}`}>
        <div className="ei-topbar-left">
          <button className="ei-back-btn" onClick={handleBackClick} aria-label="Go back">
            <i className="fas fa-arrow-left"></i>
          </button>
          <div className="ei-topbar-title-group">
            <span className="ei-topbar-eyebrow">
              {finalMode === 'edit' ? 'Editing Incubation' : 'New Incubation'}
            </span>
            <div className="ei-topbar-title-row">
              <h1 className="ei-topbar-title">
                {incubationName.trim() || 'Untitled Incubation'}
              </h1>
              <span className={`ei-status-chip ${statusMeta[incubationStatus]?.className || ''}`}>
                {statusMeta[incubationStatus]?.label || 'Unknown'}
              </span>
            </div>
          </div>
        </div>
        <div className="ei-topbar-actions">
          {anyDirty && (
            <span className="ei-unsaved-summary" aria-live="polite">
              <span className="ei-unsaved-dot" aria-hidden="true"></span>
              {dirtyCount} unsaved {dirtyCount === 1 ? 'change' : 'changes'}
            </span>
          )}
          <Button
            variant="outline"
            size="medium"
            className="ei-cancel-btn"
            onClick={handleBackClick}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="medium"
            className={`ei-save-btn ${anyDirty ? 'is-dirty' : ''}`}
            onClick={() => {
              console.log(`${finalMode} Incubation button clicked!`);
              handleSubmit();
            }}
            disabled={submitting || (finalMode === 'edit' && !anyDirty)}
            ripple
          >
            {submitting
              ? (finalMode === 'edit' ? 'Saving...' : 'Creating...')
              : (finalMode === 'edit' ? 'Save Changes' : 'Create Incubation')}
          </Button>
        </div>
      </header>

      {/* Two-column content grid */}
      <main className="ei-content">
        <div className="ei-grid">
          <div className="ei-column ei-column-left">

            {/* BASICS */}
            <section className="ei-section">
              <header className="ei-section-header">
                <div className="ei-section-heading">
                  <div className="ei-section-title-row">
                    <h2 className="ei-section-title">Basics</h2>
                    {dirty.basics && <span className="ei-dirty-pill">Unsaved</span>}
                  </div>
                  <p className="ei-section-sub">Core identity, status, and timeline</p>
                </div>
              </header>
              <div className="ei-section-body">
                  <div className="ei-field">
                    <label className="ei-field-label">Incubation Name <span className="ei-required">*</span></label>
                    <Input
                      type="text"
                      placeholder="e.g. Spring Wellness Trial"
                      value={incubationName}
                      onChange={(e) => setIncubationName(e.target.value)}
                      className={errors.incubationName ? 'form-input error' : 'form-input'}
                    />
                    {errors.incubationName && <span className="error-message">{errors.incubationName}</span>}
                  </div>

                  <div className="ei-field-grid-2">
                    <div className="ei-field">
                      <label className="ei-field-label">Status</label>
                      <Select
                        options={[
                          { value: 'pre_launch', label: 'Pre Launch' },
                          { value: 'active', label: 'Active' },
                          { value: 'completed', label: 'Completed' }
                        ]}
                        value={incubationStatus}
                        onChange={(e) => setIncubationStatus(e.target.value)}
                        className={errors.incubationStatus ? 'form-input error' : 'form-input'}
                      />
                      {errors.incubationStatus && <span className="error-message">{errors.incubationStatus}</span>}
                    </div>
                    <div className="ei-field">
                      <label className="ei-field-label">Target Completes</label>
                      <Input
                        type="number"
                        placeholder="e.g. 100"
                        value={targetCompletes}
                        onChange={(e) => setTargetCompletes(e.target.value)}
                        className="form-input"
                        min="0"
                      />
                    </div>
                  </div>

                  <div className="ei-field-grid-2">
                    <div className="ei-field">
                      <label className="ei-field-label">Start Date <span className="ei-required">*</span></label>
                      <DatePicker
                        selected={incubationStartDate}
                        onChange={(date) => setIncubationStartDate(date)}
                        dateFormat="MM/dd/yyyy"
                        placeholderText="Select start date"
                        className={errors.incubationStartDate ? 'form-input error' : 'form-input'}
                      />
                      {errors.incubationStartDate && <span className="error-message">{errors.incubationStartDate}</span>}
                    </div>
                    <div className="ei-field">
                      <label className="ei-field-label">End Date <span className="ei-required">*</span></label>
                      <DatePicker
                        selected={incubationEndDate}
                        onChange={(date) => setIncubationEndDate(date)}
                        dateFormat="MM/dd/yyyy"
                        placeholderText="Select end date"
                        className={errors.incubationEndDate ? 'form-input error' : 'form-input'}
                      />
                      {errors.incubationEndDate && <span className="error-message">{errors.incubationEndDate}</span>}
                    </div>
                  </div>
                </div>
            </section>

            {/* DESCRIPTION */}
            <section className="ei-section">
              <header className="ei-section-header">
                <div className="ei-section-heading">
                  <div className="ei-section-title-row">
                    <h2 className="ei-section-title">Description</h2>
                    {dirty.description && <span className="ei-dirty-pill">Unsaved</span>}
                  </div>
                  <p className="ei-section-sub">What this incubation is about</p>
                </div>
              </header>
              <div className="ei-section-body">
                  <div className="ei-field">
                    <label className="ei-field-label">Overview</label>
                    <Textarea
                      placeholder="A short summary that participants might see."
                      value={incubationOverview}
                      onChange={(e) => setIncubationOverview(e.target.value)}
                      className="form-input"
                    />
                  </div>
                  <div className="ei-field">
                    <label className="ei-field-label">Purpose</label>
                    <Textarea
                      placeholder="Why are you running this incubation?"
                      value={incubationPurpose}
                      onChange={(e) => setIncubationPurpose(e.target.value)}
                      className="form-input"
                    />
                  </div>
                </div>
            </section>

            {/* ELIGIBILITY */}
            <section className="ei-section">
              <header className="ei-section-header">
                <div className="ei-section-heading">
                  <div className="ei-section-title-row">
                    <h2 className="ei-section-title">Eligibility Criteria</h2>
                    {dirty.eligibility && <span className="ei-dirty-pill">Unsaved</span>}
                  </div>
                  <p className="ei-section-sub">Who qualifies, and who doesn't</p>
                </div>
                <span className="ei-count-badge">
                  {incubationInclusionCriteria.length + incubationExclusionCriteria.length}
                </span>
              </header>
              <div className="ei-section-body">
                  <div className="ei-tabs">
                    <button
                      type="button"
                      className={`ei-tab ${criteriaTab === 'inclusion' ? 'active' : ''}`}
                      onClick={() => setCriteriaTab('inclusion')}
                    >
                      Inclusion <span className="ei-tab-count">{incubationInclusionCriteria.length}</span>
                    </button>
                    <button
                      type="button"
                      className={`ei-tab ${criteriaTab === 'exclusion' ? 'active' : ''}`}
                      onClick={() => setCriteriaTab('exclusion')}
                    >
                      Exclusion <span className="ei-tab-count">{incubationExclusionCriteria.length}</span>
                    </button>
                  </div>

                  {criteriaTab === 'inclusion' ? (
                    <div className="ei-criteria-panel">
                      <div className="ei-inline-add">
                        <Input
                          type="text"
                          placeholder="Add an inclusion criterion (e.g. Dogs over 5 lbs)"
                          value={newInclusionCriteria}
                          onChange={(e) => setNewInclusionCriteria(e.target.value)}
                          onKeyPress={handleInclusionCriteriaKeyPress}
                          className="form-input ei-inline-add-field"
                        />
                        <button
                          type="button"
                          className="ei-plus-btn"
                          onClick={handleAddInclusionCriteria}
                          disabled={!newInclusionCriteria.trim()}
                          aria-label="Add inclusion criterion"
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                      {editingInclusionCriteria.length === 0 ? (
                        <div className="ei-empty">No inclusion criteria yet.</div>
                      ) : (
                        <ul className="ei-criteria-list">
                          {editingInclusionCriteria.map((criteria, index) => (
                            <li key={index} className="ei-criteria-row">
                              <span className="ei-criteria-bullet ei-criteria-bullet-incl">
                                <i className="fas fa-check"></i>
                              </span>
                              <span className="ei-criteria-text">{criteria.name}</span>
                              <Button
                                variant={inclusionCriteriaConfirmRemove[index] ? 'danger' : 'outline'}
                                size="small"
                                className={`ei-criteria-remove ${inclusionCriteriaConfirmRemove[index] ? 'confirm-remove' : ''}`}
                                onClick={() => handleInclusionCriteriaRemoveClick(index)}
                              >
                                {inclusionCriteriaConfirmRemove[index] ? 'Confirm' : 'Remove'}
                              </Button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <div className="ei-criteria-panel">
                      <div className="ei-inline-add">
                        <Input
                          type="text"
                          placeholder="Add an exclusion criterion (e.g. Pregnant pets)"
                          value={newExclusionCriteria}
                          onChange={(e) => setNewExclusionCriteria(e.target.value)}
                          onKeyPress={handleExclusionCriteriaKeyPress}
                          className="form-input ei-inline-add-field"
                        />
                        <button
                          type="button"
                          className="ei-plus-btn"
                          onClick={handleAddExclusionCriteria}
                          disabled={!newExclusionCriteria.trim()}
                          aria-label="Add exclusion criterion"
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                      {editingExclusionCriteria.length === 0 ? (
                        <div className="ei-empty">No exclusion criteria yet.</div>
                      ) : (
                        <ul className="ei-criteria-list">
                          {editingExclusionCriteria.map((criteria, index) => (
                            <li key={index} className="ei-criteria-row">
                              <span className="ei-criteria-bullet ei-criteria-bullet-excl">
                                <i className="fas fa-times"></i>
                              </span>
                              <span className="ei-criteria-text">{criteria.name}</span>
                              <Button
                                variant={exclusionCriteriaConfirmRemove[index] ? 'danger' : 'outline'}
                                size="small"
                                className={`ei-criteria-remove ${exclusionCriteriaConfirmRemove[index] ? 'confirm-remove' : ''}`}
                                onClick={() => handleExclusionCriteriaRemoveClick(index)}
                              >
                                {exclusionCriteriaConfirmRemove[index] ? 'Confirm' : 'Remove'}
                              </Button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
            </section>

          </div>

          <div className="ei-column ei-column-right">

            {/* DATA FIELDS */}
            <section className="ei-section ei-section-tall">
              <header className="ei-section-header">
                <div className="ei-section-heading">
                  <div className="ei-section-title-row">
                    <h2 className="ei-section-title">Data Fields</h2>
                    {dirty.fields && <span className="ei-dirty-pill">Unsaved</span>}
                  </div>
                  <p className="ei-section-sub">Fields collected on each participant</p>
                </div>
                <span className="ei-count-badge">{allDataFields.length}</span>
                <Button
                  variant="primary"
                  size="small"
                  className="ei-header-action-btn"
                  onClick={openCreateFieldModal}
                >
                  <i className="fas fa-plus"></i> New Field
                </Button>
              </header>
              <div className="ei-section-body">
                {/* Fields list (alphabetical, view-only; click a row to edit) */}
                <div className="ei-fields-list">
                    {allDataFields.length === 0 ? (
                      <div className="ei-empty">No data fields yet.</div>
                    ) : (
                      allDataFields.map((field, index) => {
                        if (field.isCalculated) {
                          const sourceFieldKey = field.dateSource || field.sourceField;
                          const sourceField = selectedDataFields.find(df => df.name === sourceFieldKey);
                          const sourceFieldDisplayName = sourceField?.displayName || keyToDisplayString(sourceFieldKey);
                          const sourceLabel = field.calculation === 'lbs_to_kg' ? 'from' : 'from';

                          return (
                            <div key={`calc-${index}`} className="ei-field-row ei-field-row-calc">
                              <div className="ei-field-row-content">
                                <div className="ei-field-row-name">{field.displayName}</div>
                                <div className="ei-field-row-meta">
                                  <span className="ei-field-row-type">Calculated</span>
                                  <span className="ei-field-row-sep">·</span>
                                  <span className="ei-field-row-hint">{sourceLabel} {sourceFieldDisplayName}</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="ei-field-remove-icon"
                                title="Remove"
                                aria-label="Remove calculated field"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const calcIndex = calculatedFields.findIndex(cf => cf.name === field.name && cf.calculation === field.calculation);
                                  if (calcIndex !== -1) {
                                    handleRemoveCalculatedField(calcIndex);
                                  }
                                }}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          );
                        }

                        const editingIndex = editingDataFields.findIndex(ef =>
                          ef.name === field.name && ef.displayName === field.displayName
                        );
                        if (editingIndex === -1) return null;
                        const editingField = editingDataFields[editingIndex];
                        const choicesPreview = editingField.type === 'choices' && editingField.choices && editingField.choices.length > 0
                          ? (editingField.choices.length > 4
                              ? `${editingField.choices.slice(0, 4).join(', ')}, +${editingField.choices.length - 4} more`
                              : editingField.choices.join(', '))
                          : null;

                        return (
                          <button
                            type="button"
                            key={`f-${index}`}
                            className="ei-field-row ei-field-row-clickable"
                            onClick={() => openFieldModal(editingIndex)}
                            aria-label={`Edit field ${editingField.displayName}`}
                          >
                            <div className="ei-field-row-content">
                              <div className="ei-field-row-name">{editingField.displayName}</div>
                              <div className="ei-field-row-meta">
                                <span className="ei-field-row-type">{formatTypeLabel(editingField.type)}</span>
                                {choicesPreview && (
                                  <>
                                    <span className="ei-field-row-sep">·</span>
                                    <span className="ei-field-row-hint">{choicesPreview}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <i className="fas fa-chevron-right ei-field-row-chevron"></i>
                          </button>
                        );
                      })
                    )}
                </div>
              </div>
            </section>

            {/* CALCULATED FIELDS */}
            <section className="ei-section">
              <header className="ei-section-header">
                <div className="ei-section-heading">
                  <div className="ei-section-title-row">
                    <h2 className="ei-section-title">Calculated Fields</h2>
                    {dirty.calculated && <span className="ei-dirty-pill">Unsaved</span>}
                  </div>
                  <p className="ei-section-sub">Fields derived from other fields</p>
                </div>
                <span className="ei-count-badge">{calculatedFields.length}</span>
              </header>
              <div className="ei-section-body">
                  {!showAgeField && !showWeightField && (!hasPetAgeCalc || !hasWeightKgCalc) && (
                    <div className="calculated-field-add-section ei-calc-add-section">
                      {!hasPetAgeCalc && (
                        <Button
                          variant="outline"
                          size="small"
                          className="add-calculated-field-button"
                          onClick={handleAddAgeField}
                        >
                          <i className="fas fa-plus"></i>
                          <span>Pet Age</span>
                        </Button>
                      )}
                      {!hasWeightKgCalc && (
                        <Button
                          variant="outline"
                          size="small"
                          className="add-calculated-field-button"
                          onClick={handleAddWeightField}
                        >
                          <i className="fas fa-plus"></i>
                          <span>Pet Weight (kg)</span>
                        </Button>
                      )}
                    </div>
                  )}

                  {showAgeField && (
                    <div className="calculated-field-input-container">
                      <div className="calculated-field-header">
                        <h4 className="calculated-field-title">Pet Age</h4>
                        <p className="calculated-field-description">
                          Select the date field that the pet age should be calculated from
                        </p>
                      </div>
                      <div className="calculated-field-input-row">
                        <Input
                          type="text"
                          value={ageFieldName}
                          onChange={(e) => handleAgeFieldNameChange(e.target.value)}
                          className="form-input calculated-field-name-input"
                          placeholder="Field name"
                        />
                        <Select
                          options={getDateFields()}
                          value={ageFieldDateSource}
                          onChange={(e) => handleAgeFieldDateSourceChange(e.target.value)}
                          className="calculated-field-date-select"
                          placeholder="Select date field"
                        />
                        <Button
                          variant="outline"
                          size="small"
                          className="remove-calculated-field-button"
                          onClick={() => {
                            setShowAgeField(false);
                            setAgeFieldName('Pet Age');
                            setAgeFieldDateSource('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {showWeightField && (
                    <div className="calculated-field-input-container">
                      <div className="calculated-field-header">
                        <h4 className="calculated-field-title">Pet Weight (kg)</h4>
                        <p className="calculated-field-description">
                          Select the number field (in lbs) to convert to kilograms
                        </p>
                      </div>
                      <div className="calculated-field-input-row">
                        <Input
                          type="text"
                          value={weightFieldName}
                          onChange={(e) => handleWeightFieldNameChange(e.target.value)}
                          className="form-input calculated-field-name-input"
                          placeholder="Field name"
                        />
                        <Select
                          options={getNumberFields()}
                          value={weightFieldSource}
                          onChange={(e) => handleWeightFieldSourceChange(e.target.value)}
                          className="calculated-field-date-select"
                          placeholder="Select number field (lbs)"
                        />
                        <Button
                          variant="outline"
                          size="small"
                          className="remove-calculated-field-button"
                          onClick={() => {
                            setShowWeightField(false);
                            setWeightFieldName('Pet Weight (kg)');
                            setWeightFieldSource('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {calculatedFields.length === 0 && !showAgeField && !showWeightField ? (
                    <div className="ei-empty">No calculated fields yet. Click a button above to add one.</div>
                  ) : (
                    <div className="ei-calc-list">
                      {calculatedFields.map((field, index) => {
                        const sourceFieldKey = field.dateSource || field.sourceField;
                        const sourceField = selectedDataFields.find(df => df.name === sourceFieldKey);
                        const sourceFieldDisplayName = sourceField?.displayName || keyToDisplayString(sourceFieldKey);

                        return (
                          <div key={index} className="calculated-field-item">
                            <div className="calculated-field-display">
                              <span className="calculated-field-name">{field.name}</span>
                              <span className="calculated-field-details">
                                ({field.calculation === 'lbs_to_kg' ? 'converts from' : 'calculated from'} {sourceFieldDisplayName})
                              </span>
                            </div>
                            <Button
                              variant="outline"
                              size="small"
                              className="remove-calculated-field-button"
                              onClick={() => handleRemoveCalculatedField(index)}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
              </div>
            </section>

            {/* USER ACCESS (view-only, edit mode only) */}
            {finalMode === 'edit' && (
              <section className="ei-section">
                <header className="ei-section-header">
                  <div className="ei-section-heading">
                    <div className="ei-section-title-row">
                      <h2 className="ei-section-title">User Access</h2>
                    </div>
                    <p className="ei-section-sub">Users who can access this incubation and their permissions</p>
                  </div>
                  <span className="ei-count-badge">{incubationUsers.length}</span>
                </header>
                <div className="ei-section-body">
                  {incubationUsersLoading ? (
                    <div className="ei-empty">Loading…</div>
                  ) : incubationUsers.length === 0 ? (
                    <div className="ei-empty">No users have access to this incubation yet.</div>
                  ) : (
                    <div className="ei-user-access-list">
                      {incubationUsers.map(user => (
                        <div key={user.id} className="ei-user-access-item">
                          <div className="ei-user-access-identity">
                            <div className="ei-user-access-name-row">
                              <span className="ei-user-access-name">
                                {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                              </span>
                              {user.is_global_admin && (
                                <span className="ei-user-access-admin-badge">Admin</span>
                              )}
                            </div>
                            {user.email && (
                              <span className="ei-user-access-email">{user.email}</span>
                            )}
                          </div>
                          <div className="ei-user-access-perms">
                            {(user.functionality_names || []).length === 0 ? (
                              <span className="ei-user-access-perm-empty">No permissions</span>
                            ) : (
                              user.functionality_names.map(name => (
                                <span key={name} className="ei-user-access-perm-chip">{name}</span>
                              ))
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            )}

          </div>
        </div>
      </main>

      {/* Create Field Modal */}
      {creatingField && (
        <div className="ei-modal-overlay" onClick={closeCreateFieldModal} role="dialog" aria-modal="true">
          <div className="ei-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ei-modal-header">
              <h3 className="ei-modal-title">New Field</h3>
              <button
                type="button"
                className="ei-modal-close"
                onClick={closeCreateFieldModal}
                aria-label="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="ei-modal-body">
              <div className="ei-field">
                <label className="ei-field-label">Field Name <span className="ei-required">*</span></label>
                <Input
                  type="text"
                  value={newDataField}
                  onChange={(e) => setNewDataField(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') handleCreateFieldSubmit(); }}
                  className="form-input"
                  placeholder="e.g. Pet Nickname"
                  autoFocus
                />
              </div>

              <div className="ei-field">
                <label className="ei-field-label">Type</label>
                <Select
                  options={dataFieldTypeOptions}
                  value={dataFieldType}
                  onChange={(e) => handleDataFieldTypeChangeForNew(e.target.value)}
                  className="form-input"
                />
              </div>

              {dataFieldType === 'choices' && (
                <div className="ei-field">
                  <label className="ei-field-label">
                    Choices <span className="ei-hint-text">(at least two)</span>
                  </label>
                  <div className="ei-modal-choices">
                    {newFieldChoices.map((choice, index) => (
                      <div key={index} className="choice-input-row">
                        <Input
                          type="text"
                          placeholder={`Choice ${index + 1}`}
                          value={choice}
                          onChange={(e) => handleNewFieldChoiceChange(index, e.target.value)}
                          className={`form-input choice-input ${choiceValidationErrors[index] ? 'choice-input-error' : ''}`}
                        />
                        {index >= 2 && (
                          <button
                            type="button"
                            className="choice-delete-button"
                            onClick={() => handleDeleteNewChoice(index)}
                            title="Delete choice"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        )}
                      </div>
                    ))}
                    <button type="button" className="ei-add-choice-btn" onClick={handleAddNewChoice}>
                      + Add choice
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="ei-modal-footer">
              <Button
                variant="outline"
                size="medium"
                className="ei-cancel-btn"
                onClick={closeCreateFieldModal}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="medium"
                className="ei-modal-done-btn"
                onClick={handleCreateFieldSubmit}
                disabled={!newDataField.trim()}
              >
                Create Field
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Field Modal */}
      {editingFieldIndex !== null && editingDataFields[editingFieldIndex] && (() => {
        const editingField = editingDataFields[editingFieldIndex];
        const idx = editingFieldIndex;
        const isConfirming = !!dataFieldConfirmRemove[idx];
        return (
          <div className="ei-modal-overlay" onClick={closeFieldModal} role="dialog" aria-modal="true">
            <div className="ei-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ei-modal-header">
                <h3 className="ei-modal-title">Edit Field</h3>
                <button
                  type="button"
                  className="ei-modal-close"
                  onClick={closeFieldModal}
                  aria-label="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="ei-modal-body">
                <div className="ei-field">
                  <label className="ei-field-label">Field Name</label>
                  <Input
                    type="text"
                    value={editingField.displayName}
                    onChange={(e) => handleDataFieldNameChange(idx, e.target.value)}
                    className="form-input"
                    placeholder="Field name"
                    autoFocus
                  />
                </div>

                <div className="ei-field">
                  <label className="ei-field-label">Type</label>
                  <Select
                    options={dataFieldTypeOptions}
                    value={editingField.type}
                    onChange={(e) => handleDataFieldTypeChange(idx, e.target.value)}
                    className="form-input"
                  />
                </div>

                {editingField.type === 'choices' && (
                  <div className="ei-field">
                    <label className="ei-field-label">
                      Choices <span className="ei-hint-text">(at least two)</span>
                    </label>
                    <div className="ei-modal-choices">
                      {(editingFieldChoices[idx] || editingField.choices || ['', '']).map((choice, choiceIndex) => (
                        <div key={choiceIndex} className="choice-input-row">
                          <Input
                            type="text"
                            placeholder={`Choice ${choiceIndex + 1}`}
                            value={choice}
                            onChange={(e) => handleFieldChoiceChange(idx, choiceIndex, e.target.value)}
                            className="form-input choice-input"
                          />
                          {choiceIndex >= 2 && (
                            <button
                              type="button"
                              className="choice-delete-button"
                              onClick={() => handleDeleteFieldChoice(idx, choiceIndex)}
                              title="Delete choice"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        className="ei-add-choice-btn"
                        onClick={() => handleAddFieldChoice(idx)}
                      >
                        + Add choice
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="ei-modal-footer">
                <Button
                  variant="outline"
                  size="medium"
                  className={`ei-modal-delete-btn ${isConfirming ? 'confirm-remove' : ''}`}
                  onClick={() => {
                    const wasConfirming = isConfirming;
                    handleDataFieldRemoveClick(idx);
                    if (wasConfirming) closeFieldModal();
                  }}
                >
                  {isConfirming ? 'Confirm Delete' : 'Delete Field'}
                </Button>
                <Button
                  variant="primary"
                  size="medium"
                  className="ei-modal-done-btn"
                  onClick={closeFieldModal}
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default CreateIncubationScreen;
